import ReleaseContactsDialog from '@/components/ReleaseContactsDialog';
import { AssignmentSummary } from '@/types/shared';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ChevronRight from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Chip, IconButton, Menu, MenuItem } from '@mui/material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { getStatusFromCounts } from '@/util/listStatus';
import {
  createContactsTotalFilterOperators,
  createContactsTotalSortComparator,
} from '@/util/gridFilterOperators';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import moment from 'moment';
import { useState } from 'react';
import Avatar from 'react-avatar';

export type AssignmentsColumnName =
  | 'Contacts'
  | 'Meaningful interactions'
  | 'Ellipsis'
  | 'Assignees'
  | 'Date assigned'
  | 'Status'
  | 'Actions'
  | 'Id';

type AssignmentsColumnDef = AssignmentSummary & {
  handleRelease: (id: string) => void;
  listId: number;
  listName: string;
};

type AssignmentsColumn = GridColDef<AssignmentsColumnDef, string, string>;

const contactCount: AssignmentsColumn = {
  headerName: 'Contacts',
  field: 'count',
  align: 'left',
  flex: 3,
  valueGetter: (value, row) => `${row.count - row.uncalledCount}/${row.count}`,
  filterOperators: createContactsTotalFilterOperators<AssignmentsColumnDef, string, string>(
    (row) => row.count
  ),
  sortComparator: createContactsTotalSortComparator<AssignmentsColumnDef>(
    (row) => row.count,
    (row) => row.count - row.uncalledCount
  ),
};

const assignees: AssignmentsColumn = {
  headerName: 'Assignee name',
  field: 'name',
  align: 'left',
  flex: 3,
  renderCell: (params) => {
    return (
      <span>
        <Avatar src={params.row.picture} name={params.row.name} size="40" round /> {params.row.name}
      </span>
    );
  },
};

const dateAssigned: AssignmentsColumn = {
  headerName: 'Date assigned',
  field: 'dateAssigned',
  align: 'left',
  type: 'date',
  flex: 3,
  // MUI v6 date columns require Date; valueGetter converts string to Date
  valueGetter: ((value: string) =>
    value ? new Date(value as string) : null) as unknown as AssignmentsColumn['valueGetter'],
  renderCell: (params) => (
    <span>
      {params.row.dateAssigned ? moment(params.row.dateAssigned).format('DD/MM/YYYY') : ''}
    </span>
  ),
};

const assignedSubMeaningfulCount: AssignmentsColumn = {
  headerName: 'Meaningful Interactions',
  field: 'meaningfulInteractionCount',
  align: 'left',
  type: 'number',
  headerAlign: 'left',
  flex: 3,
};

const status: AssignmentsColumn = {
  headerName: 'Status',
  field: 'uncalledCount',
  valueOptions: ['Complete', 'Not started', 'In progress'],
  type: 'singleSelect',
  align: 'left',
  flex: 3,
  valueGetter: (value, row) =>
    getStatusFromCounts({ totalCount: row.count, uncalledCount: row.uncalledCount }),
  renderCell: ({ value }) => {
    const chipColour =
      value === 'Complete' ? 'primary' : value === 'Not started' ? 'warning' : 'secondary';

    return <Chip label={value} color={chipColour} size="small" variant={'filled'} />;
  },
};

const actions: AssignmentsColumn = {
  headerName: '',
  field: 'Actions',
  align: 'right',
  type: 'actions',
  sortable: false,
  filterable: false,
  editable: false,
  headerClassName: 'lastcolumnSeparator',
  disableColumnMenu: true,
  flex: 1,
  getActions: (params: { row: AssignmentSummary & { handleRelease: (id: string) => void } }) => [
    <GridActionsCellItem
      label={'Release contacts'}
      key="Release"
      showInMenu
      icon={<ArrowRightIcon />}
      onClick={() => params.row.handleRelease(params.row.id)}
    />,
  ],
};

const id: AssignmentsColumn = {
  headerName: 'Id',
  field: 'id',
  align: 'left',
  headerAlign: 'left',
  flex: 4,
  editable: false,
  type: 'string',
};

const ellipsis: AssignmentsColumn = {
  headerName: '',
  align: 'right',
  flex: 1,
  field: 'Actions',
  sortable: false,
  filterable: false,
  editable: false,
  headerClassName: 'lastcolumnSeparator',
  disableColumnMenu: true,
  renderCell: (params) => {
    return (
      <PopupState variant="popover" popupId="demo-popup-menu">
        {(popupState) => {
          const [isReleaseContactsDialogOpen, setIsReleaseContactsDialogOpen] = useState(false);
          const [releaseContactsState, setReleaseContactsState] = useState('');

          const handleReleaseContactsDialogClose = () => {
            setIsReleaseContactsDialogOpen(false);
          };

          return (
            <>
              <IconButton
                aria-controls={`${params.row.id}-action-menu-icon`}
                id={`${params.row.id}-open-action-menu-icon`}
                data-testid={`${params.row.id}-open-action-menu-icon`}
                aria-label="Open actions menu for assignment"
                aria-haspopup="true"
                size="small"
                {...bindTrigger(popupState)}
              >
                <MoreVertIcon sx={{ fontSize: '20px' }} />
              </IconButton>

              <Menu {...bindMenu(popupState)}>
                <PopupState variant="popover" popupId="demo-popup-menu">
                  {(otherPopupState) => {
                    const triggerReleaseContactsDialogReal = (dropdownText: string) => {
                      setReleaseContactsState(dropdownText);
                      setIsReleaseContactsDialogOpen(true);
                      popupState.close();
                      otherPopupState.close();
                    };

                    return (
                      <>
                        <MenuItem
                          sx={{ display: 'flex', justifyContent: 'flex-end' }}
                          {...bindTrigger(otherPopupState)}
                          disabled={params.row.skippedCount === 0 && params.row.uncalledCount === 0}
                        >
                          Release contacts
                          <ChevronRight />
                        </MenuItem>
                        <Menu
                          {...bindMenu(otherPopupState)}
                          onClose={() => {
                            popupState.close();
                          }}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'center', horizontal: 'right' }}
                        >
                          <MenuItem
                            sx={{ display: 'flex', justifyContent: 'flex-end' }}
                            onClick={() => {
                              triggerReleaseContactsDialogReal('uncalled');
                            }}
                            disabled={params.row.uncalledCount === 0}
                          >
                            Uncalled
                          </MenuItem>
                          <MenuItem
                            sx={{ display: 'flex', justifyContent: 'flex-end' }}
                            onClick={() => {
                              triggerReleaseContactsDialogReal('skipped');
                            }}
                            disabled={params.row.skippedCount === 0}
                          >
                            Skipped
                          </MenuItem>
                          <MenuItem
                            sx={{ display: 'flex', justifyContent: 'flex-end' }}
                            onClick={() => {
                              triggerReleaseContactsDialogReal('uncalled and skipped');
                            }}
                            disabled={
                              params.row.skippedCount === 0 || params.row.uncalledCount === 0
                            }
                          >
                            Uncalled and skipped
                          </MenuItem>
                        </Menu>
                      </>
                    );
                  }}
                </PopupState>
              </Menu>

              <ReleaseContactsDialog
                isOpen={isReleaseContactsDialogOpen}
                onClose={handleReleaseContactsDialogClose}
                isACoordinator={false}
                releaseContactsState={releaseContactsState}
                disableMeToggle={true}
                toggleValue={'me'}
                setToggleValue={() => {}}
                listId={params.row.listId}
                listName={params.row.listName}
                chosenUserName={params.row.name}
                chosenUserSub={params.row.id}
              />
            </>
          );
        }}
      </PopupState>
    );
  },
};

const AssignmentsColumns: Record<AssignmentsColumnName, AssignmentsColumn> = {
  Contacts: contactCount,
  Assignees: assignees,
  'Date assigned': dateAssigned,
  Status: status,
  Actions: actions,
  Id: id,
  Ellipsis: ellipsis,
  'Meaningful interactions': assignedSubMeaningfulCount,
};

export const getAssignmentsTableColumnDefinitions = (columnNames: AssignmentsColumnName[]) => {
  return columnNames.map((column) => AssignmentsColumns[column]);
};
